import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { SupportStartFields } from './SupportStartFields';
import {
  emptySupportStart,
  supportStartSchema,
  type SupportStartInput,
  type SupportStartValues,
} from './supportStartModel';
import { SupportTwoFactorHint } from './SupportTwoFactorHint';
import { useSupportStart, type SupportTarget } from './useSupportStart';

/**
 * Bestätigung nach Ablauf der Browser-Freigabe. Erst die Zusage des Servers
 * wechselt den Workspace; bis dahin bleibt der Tab, wo er ist.
 */
export function SupportStartDialog({
  target,
  onClose,
}: {
  target: SupportTarget | null;
  onClose: () => void;
}) {
  const form = useForm<SupportStartInput, unknown, SupportStartValues>({
    resolver: zodResolver(supportStartSchema),
    defaultValues: emptySupportStart,
  });
  const flow = useSupportStart(target, onClose);
  const { reset: resetForm, resetField, setFocus } = form;
  const { reset: resetFlow, problem } = flow;

  useEffect(() => {
    if (!target) return;
    resetForm(emptySupportStart);
    resetFlow();
  }, [target, resetForm, resetFlow]);

  // Ein Code gilt genau einmal: nach einer Ablehnung steht das Feld leer für den nächsten.
  useEffect(() => {
    if (problem?.kind !== 'wrong-code') return;
    resetField('code');
    setFocus('code');
  }, [problem, resetField, setFocus]);

  const requestClose = () => {
    if (flow.pending) return;
    onClose();
  };

  return (
    <>
      <Dialog open={target !== null} onOpenChange={(open) => !open && requestClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Workspace-Zugriff bestätigen</DialogTitle>
            <DialogDescription>
              Bestätigen Sie den Zugriff auf „{target?.name}“ einmal für 48 Stunden. Danach wechseln
              Sie in dieser Browser-Anmeldung direkt zwischen Kunden-Workspaces. Ihre Handlungen
              werden weiterhin protokolliert; beim Kunden erscheinen Sie als „StayOn-Support“.
            </DialogDescription>
          </DialogHeader>
          {flow.checkingTwoFactor ? (
            <DialogBody>
              <p role="status" className="text-ink-soft text-sm">
                Zwei-Faktor-Anmeldung wird geprüft …
              </p>
            </DialogBody>
          ) : flow.twoFactorMissing ? (
            <SupportTwoFactorHint onClose={onClose} />
          ) : (
            // Die Form ist die Spalte des Dialogs: nur so scrollt der Inhalt und der Fuß mit
            // „Sitzung starten“ bleibt auf kleinen Fenstern sichtbar (§8).
            <form
              onSubmit={form.handleSubmit(flow.submit)}
              className="flex min-h-0 flex-1 flex-col"
              noValidate
            >
              <DialogBody className="space-y-4">
                <SupportStartFields form={form} problem={problem} />
              </DialogBody>
              <DialogFooter>
                <Button type="button" variant="ghost" className="min-h-11" onClick={requestClose}>
                  Abbrechen
                </Button>
                <Button type="submit" variant="brand" className="min-h-11" loading={flow.pending}>
                  Sitzung starten
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
